/**
 * Official refund-status tools. No government tool can be embedded or queried
 * automatically, so the portal links out and shows the exact inputs each one
 * asks for. Review every entry each filing season; `reviewedOn` and `source`
 * record when and where it was last checked.
 */

export type RefundInput = "ssn" | "filing_status" | "refund_amount" | "tax_year";

export type RefundAuthority = {
  code: string;
  name: string;
  toolName: string;
  lookupUrl: string;
  amendedUrl?: string;
  appUrl?: string;
  phone?: string;
  inputs: RefundInput[];
  /** Days after e-file acceptance before the tool shows anything useful. */
  checkAfterDaysEfile: number;
  checkAfterDaysPaper: number;
  /** Published typical timing, or null when the agency doesn't publish one. */
  typicalDaysEfile: number | null;
  typicalDaysPaper: number | null;
  notes: string[];
  /** True only when timing comes from the agency's own published guidance. */
  timingOfficial: boolean;
  reviewedOn: string;
  source: string;
};

export const REFUND_AUTHORITIES: Record<string, RefundAuthority> = {
  federal: {
    code: "federal",
    name: "IRS",
    toolName: "Where's My Refund?",
    lookupUrl: "https://www.irs.gov/wheres-my-refund",
    amendedUrl: "https://www.irs.gov/filing/wheres-my-amended-return",
    appUrl: "https://www.irs.gov/newsroom/irs2goapp",
    phone: "1-800-829-1954",
    inputs: ["ssn", "tax_year", "filing_status", "refund_amount"],
    checkAfterDaysEfile: 1,
    checkAfterDaysPaper: 28,
    typicalDaysEfile: 21,
    // The IRS says "6 weeks or more" for paper returns: no firm date.
    typicalDaysPaper: null,
    notes: [
      "Enter the refund amount exactly as shown on your Form 1040 (line 35a), in whole dollars.",
      "Status updates once a day, usually overnight.",
      "Returns claiming the Earned Income Tax Credit or Additional Child Tax Credit can't be refunded before mid-February by law, so those refunds usually arrive in early March.",
      "Paper returns take 6 weeks or more.",
      "Amended returns can take 3 weeks to appear and up to 16 weeks to process.",
    ],
    timingOfficial: true,
    reviewedOn: "2026-10-16",
    source: "irs.gov/wheres-my-refund",
  },
  MD: {
    code: "MD",
    name: "Maryland Comptroller",
    toolName: "Check refund status",
    lookupUrl: "https://interactive.marylandtaxes.gov/Individuals/refund/default.aspx",
    phone: "1-800-218-8160",
    inputs: ["ssn", "refund_amount"],
    checkAfterDaysEfile: 10,
    checkAfterDaysPaper: 30,
    typicalDaysEfile: null,
    typicalDaysPaper: null,
    notes: ["Use the exact refund amount from your Form 502.", "Maryland asks you to wait at least 10 days after acceptance before calling."],
    timingOfficial: false,
    reviewedOn: "2026-10-16",
    source: "marylandtaxes.gov (confirm each season)",
  },
  DC: {
    code: "DC",
    name: "DC Office of Tax and Revenue",
    toolName: "MyTax.DC.gov — Check the Status of My Refund",
    lookupUrl: "https://mytax.dc.gov/",
    phone: "(202) 727-4829",
    inputs: ["ssn", "tax_year", "refund_amount"],
    checkAfterDaysEfile: 7,
    checkAfterDaysPaper: 30,
    typicalDaysEfile: null,
    typicalDaysPaper: null,
    notes: [
      "On MyTax.DC.gov, under Individuals, choose \"Check the Status of My Refund\".",
      "The refund amount must match exactly. Status is shown only for returns filed in the last 6 months.",
    ],
    timingOfficial: false,
    reviewedOn: "2026-10-16",
    source: "otr.cfo.dc.gov/service/check-your-tax-refund-status",
  },
  VA: {
    code: "VA",
    name: "Virginia Tax",
    toolName: "Where's My Refund?",
    lookupUrl: "https://www.tax.virginia.gov/wheres-my-refund",
    phone: "(804) 367-2486",
    inputs: ["ssn", "refund_amount", "tax_year"],
    checkAfterDaysEfile: 3,
    checkAfterDaysPaper: 30,
    typicalDaysEfile: null,
    typicalDaysPaper: null,
    notes: ["Use the exact refund amount from your Form 760.", "Virginia sometimes mails identity-check letters that hold a refund until answered."],
    timingOfficial: false,
    reviewedOn: "2026-10-16",
    source: "tax.virginia.gov/wheres-my-refund (confirm each season)",
  },
};

export const FILING_STATUS_LABELS: Record<string, string> = {
  single: "Single",
  mfj: "Married filing jointly",
  mfs: "Married filing separately",
  hoh: "Head of household",
  qss: "Qualifying surviving spouse",
};

export const JURISDICTIONS = ["federal", "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"] as const;
